import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { useCms } from '../context/CmsContext'

export default function AboutPage() {
  const { siteSettings, getSection, getPage, processSteps } = useCms()

  const aboutPage = getPage('om-docuit-ink', {})

  const heroSection = {
    title: aboutPage.heroTitle || getSection('about_hero').title || 'Kunstnerisk Håndværk & Personlig Tryghed',
    subtitle: aboutPage.heroSubtitle || getSection('about_hero').subtitle || 'Velkommen til Docuit ink i Vester Sottrup nær Sønderborg – et moderne og dedikeret tatoveringsstudie, hvor din visjon forvandles til holdbar kropskunst.'
  }

  const artistSection = {
    title: aboutPage.introTitle || getSection('about_artist').title || 'Tatovøren Bag Docuit Ink',
    content: aboutPage.introContent || getSection('about_artist').content || '<p>Docuit ink er drevet af en dyb passion for tegning, formgivning og det klassiske tatoveringshåndværk. Med base i Vester Sottrup nær Sønderborg skaber jeg skræddersyet kropskunst, hvor der tages udgangspunkt i dine personlige idéer, historier og æstetiske ønsker.</p><p>Specialet omfatter alt fra fotorealistisk Black & Grey Realism og fine line botaniske motiver til custom skriftkunst og kreative cover-up transformationer. Hvert projekt tilgås med kunstnerisk omhu, ro og kompromisløs fokus på hygiejne og holdbarhed.</p>',
    buttonText: aboutPage.introButtonText || getSection('about_artist').buttonText || 'Send bookingforespørgsel',
    imageUrl: aboutPage.introImage || getSection('about_artist').imageUrl || siteSettings.artistImage
  }

  const processSection = {
    title: aboutPage.processTitle || getSection('about_process').title || 'Sådan arbejder jeg'
  }

  const artistImg = artistSection.imageUrl || siteSettings.artistImage

  return (
    <div className="min-h-screen bg-[#0f1013] text-[#f4f3f0] py-12 md:py-20">
      <SEO
        title="Om Docuit ink | Professionel Tatovør i Vester Sottrup nær Sønderborg"
        description="Læs om Docuit ink i Vester Sottrup nær Sønderborg. Jeg tilbyder skræddersyet tatoveringskunst, høj hygiejne og en tryg, personlig oplevelse fra skitse til færdig tatovering."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Banner section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-extrabold text-white mb-4">
            {heroSection.title}
          </h1>
          {heroSection.subtitle && (
            <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
              {heroSection.subtitle}
            </p>
          )}
        </div>

        {/* Tatovør / Artist Sektion med billede og beskrivelse */}
        <div className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-xl overflow-hidden border border-[#282b34] shadow-2xl">
                <img
                  src={artistImg}
                  alt="Tatovør hos Docuit ink"
                  className="w-full h-[360px] sm:h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1013]/60 via-transparent to-transparent" />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5 text-left">
              <h2 className="text-xl sm:text-3xl lg:text-4xl font-serif font-bold text-white tracking-wide">
                {artistSection.title}
              </h2>

              {artistSection.content ? (
                typeof artistSection.content === 'string' && artistSection.content.startsWith('<') ? (
                  <div
                    className="prose text-neutral-300 text-sm sm:text-base leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: artistSection.content }}
                  />
                ) : (
                  <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
                    {artistSection.content}
                  </p>
                )
              ) : null}

              <div className="pt-2 flex flex-wrap gap-3">
                <span className="px-3.5 py-1.5 bg-[#17191e] border border-[#282b34] rounded-full text-xs font-mono text-neutral-300 uppercase tracking-wider">
                  Black & Grey Realism
                </span>
                <span className="px-3.5 py-1.5 bg-[#17191e] border border-[#282b34] rounded-full text-xs font-mono text-neutral-300 uppercase tracking-wider">
                  Fine Line & Minimalisme
                </span>
                <span className="px-3.5 py-1.5 bg-[#17191e] border border-[#282b34] rounded-full text-xs font-mono text-neutral-300 uppercase tracking-wider">
                  Custom Lettering
                </span>
                <span className="px-3.5 py-1.5 bg-[#17191e] border border-[#282b34] rounded-full text-xs font-mono text-neutral-300 uppercase tracking-wider">
                  Cover-ups
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* Arbejdsproces */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-4xl font-serif font-bold text-white">
              {processSection.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-8 lg:gap-y-0">
            {processSteps.map((step, idx) => {
              const borderClasses = [
                idx % 2 === 0 ? 'sm:border-r sm:border-neutral-600' : 'sm:border-r-0',
                idx < 3 ? 'lg:border-r lg:border-neutral-600' : 'lg:border-r-0',
              ].join(' ');

              return (
                <div
                  key={`about-step-${step.step || idx}-${idx}`}
                  className={`p-6 lg:px-8 flex flex-col justify-between ${borderClasses}`}
                >
                  <div>
                    <span className="text-3xl font-serif font-extrabold text-[#cbb05e] block mb-3">
                      {step.step}
                    </span>
                    <h3 className="font-serif font-bold text-white text-base mb-2">
                      {step.title}
                    </h3>
                    <p className="text-xs text-neutral-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Location & Studio details */}
        <div className="bg-[#eae7e0] border border-[#dad5c9] text-[#141519] rounded-xl p-8 sm:p-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left shadow-sm">
          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-[#b55e34] font-bold block mb-1">Adresse</span>
            <h3 className="font-serif font-bold text-[#141519] text-lg">{siteSettings.name} Studio</h3>
            <p className="text-xs text-neutral-700 mt-1">{siteSettings.fullAddress}</p>
          </div>

          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-[#b55e34] font-bold block mb-1">Kontakt</span>
            <h3 className="font-serif font-bold text-[#141519] text-lg">Direkte Linje</h3>
            <p className="text-xs text-neutral-700 mt-1">
              Telefon: <a href={`tel:${siteSettings.phoneRaw}`} className="text-[#b55e34] font-semibold hover:underline whitespace-nowrap">{siteSettings.phone}</a>
            </p>
          </div>

          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-[#b55e34] font-bold block mb-1">CVR-nummer</span>
            <h3 className="font-serif font-bold text-[#141519] text-lg">Registrering</h3>
            <p className="text-xs text-neutral-700 mt-1">CVR: {siteSettings.cvr}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link
            to={artistSection.buttonUrl || '/booking'}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] font-bold uppercase tracking-wider text-xs rounded-full transition-colors shadow-xl"
          >
            {artistSection.buttonText || 'Send bookingforespørgsel'}
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>

      </div>
    </div>
  )
}
